import type {
  MentionDropdownOnKeyDown,
  MentionDropdownProps,
} from "@app/components/editor/input_bar/types";
import { useMentionSuggestions } from "@app/lib/swr/mentions";
import {
  Avatar,
  Chip,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Spinner,
  StarFilled,
} from "@dust-tt/sparkle";
import { Trans, useLingui } from "@lingui/react/macro";
import type React from "react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

export const MentionDropdown = forwardRef<
  MentionDropdownOnKeyDown,
  MentionDropdownProps
>(
  (
    {
      query,
      clientRect,
      command,
      onClose,
      owner,
      conversationId,
      spaceId,
      includeCurrentUser,
      select,
    },
    ref
  ) => {
    const { t } = useLingui();
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Call clientRect() on every render to get the latest position.
    // This avoids caching stale coordinates that may be invalid (0,0) when typing @ quickly after refresh.
    const triggerRect = clientRect?.();

    // Fetch suggestions from server using the query.
    // Backend handles all prioritization logic (participants, preferred agent, etc.)
    const { suggestions, isLoading } = useMentionSuggestions({
      workspaceId: owner.sId,
      conversationId,
      spaceId,
      query,
      select,
      includeCurrentUser,
    });

    const selectedItemRef = useRef<HTMLDivElement>(null);

    const selectItem = (index: number) => {
      const item = suggestions[index];

      if (item) {
        command(item);
      }
    };

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (event.key === "ArrowUp") {
          setSelectedIndex(
            (selectedIndex + suggestions.length - 1) % suggestions.length
          );
          return true;
        }

        if (event.key === "ArrowDown") {
          if (suggestions.length === 0) {
            return false;
          }
          setSelectedIndex((selectedIndex + 1) % suggestions.length);
          return true;
        }

        if (event.key === "Enter" || event.key === "Tab" || event.key === " ") {
          if (suggestions.length === 0) {
            return false;
          }
          selectItem(selectedIndex);
          return true;
        }

        return false;
      },
    }));

    // Reset the selected index when items change (e.g., when query changes).
    useEffect(() => {
      setSelectedIndex(0);
    }, [suggestions]);

    // Scroll selected item into view when selection changes.
    useEffect(() => {
      if (selectedItemRef.current) {
        selectedItemRef.current.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }, [selectedIndex]);

    // Only render the dropdown if we have a valid trigger.
    if (!triggerRect) {
      return null;
    }

    // Generate a key based on content state to force remount when content size changes significantly.
    // This ensures Radix UI recalculates collision detection and positioning.
    const contentKey = isLoading
      ? "loading"
      : suggestions.length === 0
        ? "empty"
        : `results-${suggestions.length}`;

    // Don't render the dropdown if there are no results
    if (suggestions.length === 0 && !isLoading) {
      return null;
    }

    const virtualTriggerStyle: React.CSSProperties = {
      position: "fixed",
      left: triggerRect.left,
      // On iOS based browsers, the position is not correct without adding the offsetTop.
      // Something related to the position calculation when there is a scrollable area.
      top:
        triggerRect.top +
        (typeof window === "undefined"
          ? 0
          : (window.visualViewport?.offsetTop ?? 0)),
      width: 1,
      height: triggerRect.height || 1,
      pointerEvents: "none",
      zIndex: -1,
      padding: 0,
      minWidth: 0,
      border: "none",
      background: "transparent",
    };

    return (
      <DropdownMenu open={true}>
        <DropdownMenuTrigger asChild>
          <div style={virtualTriggerStyle} />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          key={contentKey}
          // Lets a document comment field tell an Escape that closes this list from one that cancels.
          data-suggestion-list=""
          className="w-72"
          align="start"
          side="bottom"
          sideOffset={4}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onEscapeKeyDown={() => {
            onClose?.();
          }}
          onInteractOutside={() => {
            onClose?.();
          }}
        >
          {isLoading ? (
            <div className="flex h-12 w-full items-center justify-center">
              <Spinner />
            </div>
          ) : suggestions.length > 0 ? (
            <div className="max-h-60">
              {suggestions.map((suggestion, index) => (
                <DropdownMenuItem
                  key={suggestion.id}
                  ref={index === selectedIndex ? selectedItemRef : null}
                  className={cn(
                    index === selectedIndex
                      ? "text-highlight-500"
                      : "text-foreground"
                  )}
                  onClick={() => {
                    selectItem(index);
                  }}
                  onMouseEnter={() => {
                    setSelectedIndex(index);
                  }}
                >
                  <div className="flex w-full items-center">
                    <div className="flex min-w-0 flex-1 items-center gap-x-2">
                      <Avatar
                        size="xs"
                        name={suggestion.label}
                        visual={suggestion.pictureUrl}
                        isRounded={suggestion.type === "user"}
                      />
                      <span
                        className="truncate font-semibold"
                        title={suggestion.label}
                      >
                        {suggestion.label}
                      </span>
                    </div>
                    {suggestion.type === "agent" && suggestion.userFavorite && (
                      <Icon
                        visual={StarFilled}
                        size="xs"
                        className="ml-2 shrink-0 text-muted-foreground"
                      />
                    )}
                    {suggestion.type === "user" && (
                      <Chip
                        size="mini"
                        color="primary"
                        label={t`Member`}
                        className="ml-2 shrink-0"
                      />
                    )}
                  </div>
                </DropdownMenuItem>
              ))}
            </div>
          ) : (
            <div className="flex h-12 w-full items-center justify-center text-sm text-muted-foreground">
              <Trans>No result</Trans>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
);

MentionDropdown.displayName = "MentionDropdown";
